import styled from '@emotion/styled';

export const Container = styled.div`
    width: 100%;
    padding: ${({ theme }) => theme.spacing[4]};
`;

export const Content = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[4]};
    margin-top: ${({ theme }) => theme.spacing[2]};
`;

export const EmptyStateBox = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border: 2px dashed ${({ theme }) => theme.color.grey200};
    border-radius: ${({ theme }) => theme.radius.main};
    padding: ${({ theme }) => theme.spacing[6]};
`;

export const TerritoryRow = styled.label`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[3]};
    padding: ${({ theme }) => theme.spacing[3]};
    border-bottom: 1px solid ${({ theme }) => theme.color.grey200};
    cursor: pointer;

    &:last-child {
        border-bottom: none;
    }

    &:hover {
        background-color: ${({ theme }) => theme.color.grey100};
    }

    &[data-disabled='true'] {
        opacity: 0.4;
        cursor: not-allowed;

        &:hover {
            background-color: transparent;
        }
    }
`;

export const TerritoryInfo = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
`;

export const TerritoryName = styled.span`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[2]};
    font-size: ${({ theme }) => theme.font.size.normal};
    font-weight: ${({ theme }) => theme.font.weight.bold};
    color: ${({ theme }) => theme.color.black400};
`;

export const TerritoryCode = styled.span`
    font-weight: ${({ theme }) => theme.font.weight.regular};
    color: ${({ theme }) => theme.color.black200};
`;

export const TerritoryMeta = styled.span`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[2]};
    font-size: ${({ theme }) => theme.font.size.small};
    color: ${({ theme }) => theme.color.black200};
`;

export const Flag = styled.span`
    font-size: 24px;
    line-height: 1;
`;

export const FlagContainer = styled.div<{ size?: 'small' | 'large' }>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${({ size }) => (size === 'large' ? '64px' : '48px')};
    height: ${({ size }) => (size === 'large' ? '64px' : '48px')};
    background-color: ${({ theme }) => theme.color.grey100};
    border-radius: ${({ theme }) => theme.radius.main};
    font-size: ${({ size }) => (size === 'large' ? '32px' : '24px')};
    flex-shrink: 0;
`;

export const ListHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: ${({ theme }) => theme.spacing[2]} 0;
`;

export const SelectedCount = styled.span`
    font-size: ${({ theme }) => theme.font.size.small};
    color: ${({ theme }) => theme.color.black200};
`;

// Warning Banner
export const WarningBanner = styled.div`
    display: flex;
    align-items: flex-start;
    gap: ${({ theme }) => theme.spacing[2]};
    padding: ${({ theme }) => theme.spacing[3]};
    background-color: ${({ theme }) => theme.color.io.warning100};
    border: 1px solid ${({ theme }) => theme.color.io.warning500};
    border-radius: ${({ theme }) => theme.radius.main};

    & > i {
        color: ${({ theme }) => theme.color.io.warning500};
        font-size: 20px;
        flex-shrink: 0;
    }
`;

// Info Banner (same shape as WarningBanner, blue accent)
export const InfoBanner = styled.div`
    display: flex;
    align-items: flex-start;
    gap: ${({ theme }) => theme.spacing[2]};
    padding: ${({ theme }) => theme.spacing[3]};
    background-color: ${({ theme }) => theme.color.io.primary50};
    border: 1px solid ${({ theme }) => theme.color.electricBlue};
    border-radius: ${({ theme }) => theme.radius.main};
    margin-bottom: ${({ theme }) => theme.spacing[3]};

    & > i {
        color: ${({ theme }) => theme.color.electricBlue};
        font-size: 20px;
        flex-shrink: 0;
    }
`;

export const SupportLink = styled.a`
    color: ${({ theme }) => theme.color.electricBlue};
    font-weight: ${({ theme }) => theme.font.weight.bold};
    text-decoration: underline;
    cursor: pointer;
`;

export const WarningContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[1]};
`;

export const WarningTitle = styled.span`
    font-size: ${({ theme }) => theme.font.size.normal};
    font-weight: ${({ theme }) => theme.font.weight.bold};
    color: ${({ theme }) => theme.color.black400};
`;

export const WarningText = styled.span`
    font-size: ${({ theme }) => theme.font.size.small};
    color: ${({ theme }) => theme.color.black200};
`;

// Primary Zone Card
export const PrimaryZoneCard = styled.div`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[4]};
    padding: ${({ theme }) => theme.spacing[4]};
    border: 1px solid ${({ theme }) => theme.color.grey200};
    border-radius: ${({ theme }) => theme.radius.main};
`;

export const PrimaryZoneInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[2]};
    flex: 1;
`;

export const PrimaryZoneHeader = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: ${({ theme }) => theme.spacing[1]};
`;

export const PrimaryZoneName = styled.span`
    font-size: ${({ theme }) => theme.font.size.large};
    font-weight: ${({ theme }) => theme.font.weight.bold};
    color: ${({ theme }) => theme.color.black400};
`;

export const PrimaryZoneBadge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px ${({ theme }) => theme.spacing[2]};
    background-color: ${({ theme }) => theme.color.io.orange500};
    color: ${({ theme }) => theme.color.white100};
    font-size: ${({ theme }) => theme.font.size.small};
    font-weight: ${({ theme }) => theme.font.weight.bold};
    border-radius: ${({ theme }) => theme.radius.main};
    white-space: nowrap;

    & > i {
        font-size: 12px;
    }
`;

export const InfoFields = styled.div`
    display: flex;
    gap: ${({ theme }) => theme.spacing[4]};
`;

export const InfoField = styled.div`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[1]};
    font-size: ${({ theme }) => theme.font.size.small};
    color: ${({ theme }) => theme.color.black200};
`;

export const InfoFieldValue = styled.span`
    font-style: italic;
    color: ${({ theme }) => theme.color.black200};
`;

// Territory list badges
export const PrimaryBadge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 2px;
    padding: 1px ${({ theme }) => theme.spacing[1]};
    background-color: transparent;
    border: 1px solid ${({ theme }) => theme.color.io.orange500};
    color: ${({ theme }) => theme.color.io.orange500};
    font-size: 10px;
    font-weight: ${({ theme }) => theme.font.weight.bold};
    border-radius: ${({ theme }) => theme.radius.main};
    white-space: nowrap;

    & > i {
        font-size: 10px;
    }
`;

export const ToConfigureText = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: ${({ theme }) => theme.color.io.orange500};
    font-size: ${({ theme }) => theme.font.size.small};
    font-weight: ${({ theme }) => theme.font.weight.bold};

    & > i {
        font-size: 14px;
    }
`;

export const ConfiguredText = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: ${({ theme }) => theme.color.io.green500};
    font-size: ${({ theme }) => theme.font.size.small};
    font-weight: ${({ theme }) => theme.font.weight.bold};

    & > i {
        font-size: 14px;
    }
`;

export const ConfigureButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[1]};
    padding: ${({ theme }) => theme.spacing[1]} ${({ theme }) => theme.spacing[3]};
    border: 1px solid ${({ theme }) => theme.color.grey200};
    border-radius: ${({ theme }) => theme.radius.main};
    background-color: ${({ theme }) => theme.color.white100};
    color: ${({ theme }) => theme.color.black400};
    font-size: ${({ theme }) => theme.font.size.small};
    cursor: pointer;
    white-space: nowrap;
    flex-shrink: 0;

    &:hover {
        background-color: ${({ theme }) => theme.color.grey100};
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.4;

        &:hover {
            background-color: ${({ theme }) => theme.color.white100};
        }
    }

    & > i {
        font-size: 16px;
    }
`;

export const ViewVatRatesButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[1]};
    padding: ${({ theme }) => theme.spacing[1]} ${({ theme }) => theme.spacing[3]};
    border: 1px solid ${({ theme }) => theme.color.io.orange500};
    border-radius: ${({ theme }) => theme.radius.main};
    background-color: ${({ theme }) => theme.color.white100};
    color: ${({ theme }) => theme.color.io.orange500};
    font-size: ${({ theme }) => theme.font.size.small};
    cursor: pointer;
    white-space: nowrap;
    flex-shrink: 0;

    &:hover {
        background-color: ${({ theme }) => theme.color.io.warning100};
    }

    & > i {
        font-size: 16px;
    }
`;

export const InfoFieldIcon = styled.i`
    font-size: 14px;
    color: ${({ theme }) => theme.color.black200};
`;

// Secondary Zones Section
export const SecondaryZonesSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[2]};
    margin-top: ${({ theme }) => theme.spacing[3]};
`;

export const SecondaryZonesTitle = styled.span`
    font-size: ${({ theme }) => theme.font.size.small};
    font-weight: ${({ theme }) => theme.font.weight.bold};
    color: ${({ theme }) => theme.color.black200};
    text-transform: uppercase;
`;

export const SecondaryZoneChips = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: ${({ theme }) => theme.spacing[2]};
`;

export const SecondaryZoneChip = styled.div`
    display: inline-flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[1]};
    padding: ${({ theme }) => theme.spacing[1]} ${({ theme }) => theme.spacing[2]};
    background-color: ${({ theme }) => theme.color.grey100};
    border-radius: ${({ theme }) => theme.radius.main};
    font-size: ${({ theme }) => theme.font.size.small};
    color: ${({ theme }) => theme.color.black400};
`;

export const StarButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    cursor: pointer;
    padding: ${({ theme }) => theme.spacing[1]};
    color: ${({ theme }) => theme.color.black200};
    flex-shrink: 0;

    &:hover {
        color: ${({ theme }) => theme.color.io.orange500};
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.4;

        &:hover {
            color: ${({ theme }) => theme.color.black200};
        }
    }

    & > i {
        font-size: 18px;
    }
`;
